<script setup>
import {computed, ref, onMounted, watch} from 'vue'
import {useComponentsStore} from '@/stores/components'
import {useComponentStructureStore} from '@/stores/componentStructure'
import {useViewStore} from '@/stores/view'
import TableCell from '@/common/table-cell/TableCell'
import api from '@/services/api'

const props = defineProps({
    formId: String,
    field: Object,
    currentData: Object,
})

// Pinia Stores
const componentsStore = useComponentsStore()
const componentStructureStore = useComponentStructureStore()

// Reactive data
const viewId = ref('')
const viewStore = ref(null)
const items = ref([])

// Computed properties
const listingName = computed(() => {
    return tableName.value + '.' + props.field.name
})

const column = computed(() => {
    return componentStructureStore.getItem(props.field.tableTargetColumn)
})

const componentId = computed(() => {
    return column.value?.modulecomponentsID || column.value?.modulecomponentsId
})

const tableName = computed(() => {
    return componentsStore.items.find(o => o.id === componentId.value)?.tableName || ''
})

const columns = computed(() => {
    if (!componentId.value) return []
    return componentStructureStore.get(componentId.value).filter(o => o.browser)
})

// Fetch items helper
const fetchItems = async () => {
    if (!tableName.value || !column.value?.columnName || !props.currentData?.id) return

    try {
        const response = await api.get(`api/component/${tableName.value}`, {
            params: {
                [column.value.columnName]: props.currentData.id
            }
        })
        items.value = response.data.items || []
    } catch (error) {
        console.error("Failed to fetch table items:", error)
    }
}

// Initialize View Store when listingName resolves
const initView = () => {
    if (!listingName.value) return

    const targetViewId = 'view:' + listingName.value
    viewId.value = targetViewId

    viewStore.value = useViewStore(targetViewId)
    viewStore.value.init({
        listingName: listingName.value,
    })
}

// Watch for listingName changes to ensure store initialization
watch(listingName, () => {
    initView()
}, {immediate: true})

// Trigger data load on mount and when dependencies update
watch([tableName, () => column.value?.columnName, () => props.currentData?.id], () => {
    fetchItems()
})

onMounted(() => {
    fetchItems()
})
</script>

<template>
    <div v-if="viewId" class="card">
        <div class="card-body">
            <table class="table table-sm mb-0">
                <thead>
                <tr>
                    <th v-for="col in columns" :key="col.id">{{ col.title }}</th>
                </tr>
                </thead>

                <tbody>
                <tr v-for="item in items" :key="item.id">
                    <td v-for="col in columns" :key="col.id">
                        <TableCell :id="item.id" :column-id="col.id" :view-id="viewId"/>
                    </td>
                </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<style scoped>
.table {
    font-size: .8rem;
}
</style>